<template>
  <a-form layout="vertical" ref="formRef" :model="formState">
    <a-form-item name="image_src" label="Imagen adjunta" required>
      <ArchivePicker
        :value="formState.image_src"
        @update:url="handleSetImageSrc"
        uploadToEmail
        :maxMBSize="5"
        :supportedTypes="['.jpg', '.jpeg', '.png']"
      />
    </a-form-item>

    <a-form-item name="url" label="Enlace">
      <a-input v-model:value="formState.url" placeholder="Pega aquí el enlace" />
    </a-form-item>

    <div class="dimensions" v-if="formState.image_src">
      <a-form-item name="width" label="Ancho">
        <a-input-number
          :value="formState.width"
          @update:value="handleChangeWidth"
          :min="9"
          :precision="2"
        />
      </a-form-item>

      <LinkOutlined class="icon" />

      <a-form-item name="height" label="Alto">
        <a-input-number
          :value="formState.height"
          @update:value="handleChangeHeight"
          :min="9"
          :precision="2"
        />
      </a-form-item>
    </div>

    <a-form-item name="alignment" label="Alineación">
      <a-select
        :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        v-model:value="formState.alignment"
      >
        <a-select-option :value="ItemAlignment.Center">Centro</a-select-option>
        <a-select-option :value="ItemAlignment.Left">Izquierda</a-select-option>
        <a-select-option :value="ItemAlignment.Right">Derecha</a-select-option>
      </a-select>
    </a-form-item>
  </a-form>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { FormInstance } from "ant-design-vue";
import { LinkOutlined } from "@ant-design/icons-vue";

import { ImageItem, ItemAlignment } from "@/@types/flows/emailTemplates/items";

import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";

const props = defineProps<{ item: ImageItem }>();

const formRef = ref<FormInstance>();
const formState: Partial<ImageItem> = reactive({
  alignment: props.item.alignment,
  url: props.item.url,
  image_src: props.item.image_src,
  height: props.item.height,
  width: props.item.width,
});

const handleSetImageSrc = (imageSrc: string) => {
  if (imageSrc) {
    const imgElement = new Image();
    imgElement.src = imageSrc;

    imgElement.onload = () => {
      formState.height = imgElement.height;
      formState.width = imgElement.width;
      formState.image_src = imageSrc;
    };

    imgElement.onerror = (error) => {
      throw new Error("Failed to load image");
    };
  } else {
    formState.height = undefined;
    formState.width = undefined;
    formState.image_src = undefined;
  }
};

const aspectRatio = computed(() => {
  if (!formState.height || !formState.width) return undefined;

  return formState.height / formState.width;
});

const handleChangeWidth = (width: number) => {
  let calculatedHeight = 0;

  if (aspectRatio.value) calculatedHeight = aspectRatio.value * width;

  formState.width = width;
  if (calculatedHeight) formState.height = calculatedHeight;
};

const handleChangeHeight = (height: number) => {
  let calculatedWidth = 0;

  if (aspectRatio.value) calculatedWidth = Math.pow(aspectRatio.value, -1) * height;

  formState.height = height;
  if (calculatedWidth) formState.width = calculatedWidth;
};

const getValidatedFormState = async () => {
  await formRef.value?.validateFields();

  return formState;
};

defineExpose({ getValidatedFormState });
</script>

<style scoped lang="sass">
.dimensions
  margin-bottom: 24px
  display: flex
  align-items: center
  gap: 12px

  .ant-form-item
    margin: 0
    flex: 1 1 auto
    width: 50%

  .icon
    font-size: 24px
    color: $gray-6
    margin-top: 24px

:deep(.dimensions .ant-input-number)
  width: 100%
</style>
